/**
 * Turn HTML (a web ticket comment, a ticket template, a knowledge base article)
 * into plain text that keeps its line structure — paragraphs, line breaks and
 * list items become new lines, entities are decoded, runs of spaces collapse.
 *
 * Unlike `htmlToText()` in utils/sanitize-html.ts (a one-line preview), this keeps
 * the lines, because a ticket reply and an inserted template are read as written.
 *
 * Plain text in (a reply written in the app) comes back with only its line
 * endings normalised, so a multi-line reply still reads as typed.
 *
 * Pure string work (no DOM), so it runs the same in a worker, a test and the app.
 */

const NAMED_ENTITIES: Record<string, string> = {
  nbsp: ' ',
  amp: '&',
  lt: '<',
  gt: '>',
  quot: '"',
  apos: "'",
  '#39': "'",
  hellip: '…',
  mdash: '—',
  ndash: '–',
  rsquo: '’',
  lsquo: '‘',
  rdquo: '”',
  ldquo: '“',
  bull: '•',
};

function decodeEntities(text: string): string {
  return text.replace(/&(#x[0-9a-f]+|#\d+|[a-z]+\d*);/gi, (match, code: string) => {
    const lower = code.toLowerCase();
    if (lower.startsWith('#x')) {
      const n = Number.parseInt(lower.slice(2), 16);
      return isValidCodePoint(n) ? String.fromCodePoint(n) : match;
    }
    if (lower.startsWith('#') && lower !== '#39') {
      const n = Number.parseInt(lower.slice(1), 10);
      return isValidCodePoint(n) ? String.fromCodePoint(n) : match;
    }
    return NAMED_ENTITIES[lower] ?? match;
  });
}

/** `String.fromCodePoint()` throws outside Unicode's scalar-value range. */
function isValidCodePoint(value: number): boolean {
  return Number.isFinite(value) && value >= 0 && value <= 0x10ffff && !(value >= 0xd800 && value <= 0xdfff);
}

/** Does the string carry markup at all? */
export function looksLikeHtml(value: string): boolean {
  return /<\s*\/?\s*[a-z][^>]*>/i.test(value);
}

export function htmlToLines(input: unknown): string {
  if (typeof input !== 'string' || input === '') return '';

  if (!looksLikeHtml(input)) {
    return decodeEntities(input.replace(/\r\n?/g, '\n')).replace(/[ \t]+\n/g, '\n').trim();
  }

  let text = input.replace(/\r\n?/g, '\n');
  // Markup the text never shows.
  text = text.replace(/<(script|style|head)[^>]*>[\s\S]*?<\/\1>/gi, '');
  // In HTML a raw newline is just whitespace.
  text = text.replace(/\n/g, ' ');
  text = text.replace(/<br\s*\/?>/gi, '\n');
  text = text.replace(/<li[^>]*>/gi, '\n• ');
  text = text.replace(/<\/(p|div|h[1-6]|li|ul|ol|blockquote|pre|tr|table|section|article)>/gi, '\n');
  text = text.replace(/<(p|div|h[1-6]|ul|ol|blockquote|pre|tr|table|section|article)[^>]*>/gi, '\n');
  text = text.replace(/<[^>]*>/g, '');
  text = decodeEntities(text);

  return text
    .split('\n')
    .map((line) => line.replace(/[ \t ]+/g, ' ').trim())
    .join('\n')
    .replace(/\n{3,}/g, '\n\n')
    .trim();
}
